import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions, useContactOptions } from '@/features/crm/api';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useSaveTicket } from './api';
import { TICKET_PRIORITY_LABELS, TICKET_SOURCE_LABELS, TICKET_TYPE_LABELS } from './labels';
import type { Ticket } from './types';

export function TicketDialog({
  ticket,
  onClose,
  onSaved,
}: {
  ticket?: Ticket;
  onClose: () => void;
  onSaved?: (ticket: Ticket) => void;
}) {
  const save = useSaveTicket();
  const { data: companies = [] } = useCompanyOptions();
  const { data: users = [] } = useUserOptions();

  const [form, setForm] = useState({
    subject: ticket?.subject ?? '',
    description: ticket?.description ?? '',
    companyId: ticket?.companyId ?? '',
    contactId: ticket?.contactId ?? '',
    ticketType: ticket?.ticketType ?? 'INCIDENT',
    category: ticket?.category ?? '',
    priority: ticket?.priority ?? 'NORMAL',
    impact: ticket?.impact ?? '',
    assigneeId: ticket?.assigneeId ?? '',
    source: ticket?.source ?? 'INTERNAL',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: contacts = [] } = useContactOptions(form.companyId || undefined);
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{ticket ? `Edit ${ticket.number}` : 'New ticket'}</DialogTitle>
          <DialogDescription>
            The SLA is picked from the priority and the client&rsquo;s contract, so both are worth
            setting correctly — changing the priority later redraws the clock from when the ticket
            was raised.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={form.subject}
              placeholder="What is wrong, in one line"
              onChange={(event) => set({ subject: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>What happened</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Select
                value={form.companyId}
                onChange={(event) => set({ companyId: event.target.value, contactId: '' })}
              >
                <option value="">Choose a client</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                A live support contract is attached automatically.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Contact</Label>
              <Select
                value={form.contactId}
                onChange={(event) => set({ contactId: event.target.value })}
                disabled={!form.companyId}
              >
                <option value="">Nobody in particular</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.ticketType}
                onChange={(event) => set({ ticketType: event.target.value as never })}
              >
                {Object.entries(TICKET_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onChange={(event) => set({ priority: event.target.value as never })}
              >
                {Object.entries(TICKET_PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input
                value={form.category}
                onChange={(event) => set({ category: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Came in via</Label>
              <Select
                value={form.source}
                onChange={(event) => set({ source: event.target.value as never })}
              >
                {Object.entries(TICKET_SOURCE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select
                value={form.assigneeId}
                onChange={(event) => set({ assigneeId: event.target.value })}
              >
                <option value="">Leave unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Impact</Label>
              <Input
                value={form.impact}
                placeholder="Who or what is affected"
                onChange={(event) => set({ impact: event.target.value })}
              />
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              save.isPending ||
              form.subject.trim().length < 3 ||
              form.description.trim().length < 3 ||
              !form.companyId
            }
            onClick={async () => {
              setError(null);
              try {
                const saved = await save.mutateAsync({
                  id: ticket?.id,
                  subject: form.subject,
                  description: form.description,
                  companyId: form.companyId,
                  contactId: form.contactId || undefined,
                  ticketType: form.ticketType,
                  category: form.category || undefined,
                  priority: form.priority,
                  impact: form.impact || undefined,
                  assigneeId: form.assigneeId || undefined,
                  source: form.source,
                });
                onSaved?.(saved);
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save the ticket.',
                );
              }
            }}
          >
            {save.isPending ? 'Saving…' : ticket ? 'Save changes' : 'Raise ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
